import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, Eye, ChevronRight, Check, X, AlertCircle, Package, Truck, ClipboardCheck, FileText, Download, ArrowUpDown } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { fmtDate, fmtDateTime, istToday, istDaysAgo } from "@/lib/dateFormat";
import { MultiSelectDropdown, SelectedTags } from "@/components/ui/multi-select-dropdown";

const FALLBACK_STATUSES = [];
const FALLBACK_PIPELINE = ["Draft", "Pending with Manager", "Pending with Admin", "Pending with Finance", "At Warehouse", "Under Processing", "Ready for Dispatch", "Vehicle Assigned", "Invoice Uploaded", "Dispatched", "Delivered", "Closure Pending", "Order Closed"];

const SC = {
  "Draft": "bg-zinc-200 text-zinc-700", "Pending with Manager": "bg-purple-100 text-purple-800 border border-purple-200",
  "Pending with Admin": "bg-amber-100 text-amber-800 border border-amber-200", "Pending with Finance": "bg-blue-100 text-blue-800 border border-blue-200",
  "At Warehouse": "bg-orange-100 text-orange-800 border border-orange-200", "Under Processing": "bg-amber-100 text-amber-800 border border-amber-200",
  "Ready for Dispatch": "bg-orange-200 text-orange-900 border border-orange-300", "Vehicle Assigned": "bg-teal-100 text-teal-700 border border-teal-200",
  "Invoice Uploaded": "bg-violet-100 text-violet-800 border border-violet-200", "Dispatched": "bg-teal-200 text-teal-900 border border-teal-300",
  "Delivered": "bg-emerald-100 text-emerald-800 border border-emerald-200", "Closure Pending": "bg-pink-100 text-pink-800 border border-pink-200",
  "Order Closed": "bg-green-200 text-green-900 border border-green-300", "Rejected": "bg-red-100 text-red-800 border border-red-200",
  "Cancelled": "bg-red-200 text-red-900 border border-red-300",
  // Legacy
  "Ready for Pickup": "bg-orange-200 text-orange-900", "Shipped": "bg-teal-100 text-teal-800", "Closure Requested": "bg-pink-100 text-pink-800",
};
// FINANCE BATCH 1 (1.4c): four payment status words only
const PC = { "Unpaid": "bg-zinc-100 text-zinc-700", "Partially Paid": "bg-amber-100 text-amber-800", "Fully Paid": "bg-green-100 text-green-800", "Short Paid": "bg-orange-100 text-orange-800" };

const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.round((n || 0) * 100) / 100);
const PRIO_BADGE = { urgent: "bg-amber-100 text-amber-800 border border-amber-200", critical: "bg-red-100 text-red-800 border border-red-200" };
const PRIO_ORDER = { critical: 0, urgent: 1, standard: 2 };

// ─── STATUS TIMELINE STEPPER ──────────────────────────────
function StatusTimeline({ currentStatus, statusHistory, pipelineStages }) {
  const STAGES = pipelineStages || FALLBACK_PIPELINE;
  const rejected = currentStatus === "Rejected";
  const cancelled = currentStatus === "Cancelled";
  const currentIdx = STAGES.indexOf(currentStatus);
  const historyStatuses = new Set((statusHistory || []).map(h => h.status));

  return (
    <div className="flex items-center gap-0.5 overflow-x-auto pb-2 mb-4" data-testid="status-timeline">
      {STAGES.map((stage, i) => {
        const isPast = historyStatuses.has(stage) || i < currentIdx;
        const isCurrent = stage === currentStatus;
        const isSkipped = stage === "Pending with Admin" && !historyStatuses.has(stage) && currentIdx > 2;
        if (isSkipped) return null;
        return (
          <div key={stage} className="flex items-center shrink-0">
            <div className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] font-semibold ${
              isCurrent ? (SC[stage]?.replace('border ', '') || 'bg-zinc-900 text-white') :
              isPast ? 'bg-zinc-200 text-zinc-600' : 'bg-zinc-50 text-zinc-300'
            }`}>
              {isPast && !isCurrent ? <Check size={9} /> : null}
              {stage.replace("Pending with ", "").replace("Closure Requested", "Closure")}
            </div>
            {i < STAGES.length - 1 && <div className={`w-3 h-px mx-0.5 ${isPast ? 'bg-zinc-400' : 'bg-zinc-200'}`} />}
          </div>
        );
      })}
      {rejected && <div className="flex items-center shrink-0"><div className="w-3 h-px bg-red-300 mx-0.5" /><div className="px-2 py-1 rounded-sm text-[9px] font-semibold bg-red-100 text-red-800">Rejected</div></div>}
      {cancelled && <div className="flex items-center shrink-0"><div className="w-3 h-px bg-red-300 mx-0.5" /><div className="px-2 py-1 rounded-sm text-[9px] font-semibold bg-red-200 text-red-900">Cancelled</div></div>}
    </div>
  );
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const ORDER_STATUSES = getValues("order_statuses");
  const PAYMENT_STATUSES = getValues("payment_statuses");
  const CUSTOMER_TYPES = getValues("customer_types");
  const TRADE_FORMATS = getValues("channel_types");
  const PIPELINE_STAGES = ORDER_STATUSES.filter(s => !["Rejected", "Cancelled"].includes(s));
  const roles = user?.roles || [];
  const role = roles[0] || "";
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState((searchParams.get("status") || "").replace(/\+/g, " "));
  const [specialFilter, setSpecialFilter] = useState(searchParams.get("filter") || "");
  const [channelFilter, setChannelFilter] = useState("");
  const [tradeFormatFilter, setTradeFormatFilter] = useState([]);
  const [channelMultiFilter, setChannelMultiFilter] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState("");
  const [customerTypeFilter, setCustomerTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");
  const [channels, setChannels] = useState([]);
  const [page, setPage] = useState(1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [bulkSearchText, setBulkSearchText] = useState("");
  const [bulkSearchActive, setBulkSearchActive] = useState(false);
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [salesRepFilter, setSalesRepFilter] = useState("");
  const [datePreset, setDatePreset] = useState("");

  // Load channels for filter dropdown
  useEffect(() => { api.get("/channels").then(r => setChannels(r.data || [])).catch(err => console.error("API get /channels error:", err)); }, []);
  const [selectedOrder, setSelectedOrder] = useState(null); // kept for compatibility
  const [showDetail, setShowDetail] = useState(false);

  // Action form state
  const [actionNote, setActionNote] = useState("");
  const [actionAwb, setActionAwb] = useState("");
  const [actionCourier, setActionCourier] = useState("");
  const [actionBoxes, setActionBoxes] = useState("");

  // Inactive customers view (loaded from dashboard API, not orders)
  const [inactiveCustomers, setInactiveCustomers] = useState([]);
  const [inactiveLoading, setInactiveLoading] = useState(false);

  useEffect(() => {
    if (specialFilter === "inactive_customers") {
      setInactiveLoading(true);
      api.get("/dashboard/queues/inactive-customers").then(r => setInactiveCustomers(r.data?.data || [])).catch(err => console.error("API get /dashboard/queues/inactive-cus error:", err)).finally(() => setInactiveLoading(false));
    }
  }, [specialFilter]);

  // Compute effective channel filter from multi-select
  const effectiveChannelId = channelMultiFilter.length > 0 ? channelMultiFilter.join(",") : (channelFilter || undefined);
  // Cascading: filter channels by selected trade formats
  const filteredChannels = tradeFormatFilter.length > 0 ? channels.filter(c => tradeFormatFilter.includes(c.type)) : channels;
  const channelOptions = filteredChannels.filter(c => c.status === "active").map(c => ({ value: c._id, label: c.name, group: c.type || "Other" }));

  const loadSeq = useRef(0);
  const loadOrders = async () => {
    const seq = ++loadSeq.current;
    try {
      const params = {
        search: search || undefined, status: statusFilter || undefined,
        filter: specialFilter || undefined, channel_id: effectiveChannelId,
        payment_status: paymentFilter || undefined, customer_type: customerTypeFilter || undefined,
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
        order_numbers: bulkSearchActive ? bulkSearchText : undefined,
        sku_id: searchParams.get("sku_id") || undefined,
        sales_rep_id: salesRepFilter || undefined,     // FOLLOW-UP 3 (8e)
        page, limit: 50,
      };
      const { data } = await api.get("/orders", { params });
      if (seq !== loadSeq.current) return;        // a newer request has already answered
      setOrders(data.orders || []); setTotal(data.total || 0);
    } catch (err) { console.error("API get /orders error:", err); } finally { setLoading(false); }
  };
  // one fetch per change: a new filter goes back to page 1 first, then the list is loaded once
  const filterKey = [search, statusFilter, specialFilter, effectiveChannelId, paymentFilter,
                     customerTypeFilter, dateFrom, dateTo, salesRepFilter, bulkSearchActive].join("|");
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      if (page !== 1) { setPage(1); return; }
    }
    loadOrders();
  }, [filterKey, page]);

  const openDetail = (order) => navigate(`/orders/${order._id}`);

  // CR-7/8/9/12: Sort helper
  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
  const SH = ({ col, children, className: cls }) => (<th className={`cursor-pointer select-none hover:bg-zinc-50 ${cls || ''}`} onClick={() => toggleSort(col)}>{children} {sortCol === col && <span className="text-[9px]">{sortDir === "asc" ? "\u2191" : "\u2193"}</span>}</th>);

  // CR-9/12: Apply client-side sort and sales rep filter
  const displayOrders = (() => {
    let filtered = orders;
    // FOLLOW-UP 3 (8e): the Sales Rep filter is applied by the server, over every page
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const m = {
        order: o => parseInt((o.order_number || "").replace(/\D/g, "")) || 0,
        customer: o => (o.customer_name || "").toLowerCase(),
        customer_type: o => (o.customer_type || "").toLowerCase(),
        dispatch_by: o => o.dispatch_date || "9999",
        po_expiry: o => o.po_expiry_date || "9999",
        due_date: o => o.due_date || "9999",        // FOLLOW-UP 4 (7a): the Batch 3 due date
        sales_rep: o => (o.sales_rep_name || o.created_by_name || "").toLowerCase(),
        priority: o => PRIO_ORDER[o.priority || "standard"] ?? 2,
      };
      const fn = m[sortCol]; if (!fn) return 0;
      // FOLLOW-UP 4 (7a): rows with no due date sit at the bottom whichever way you sort
      if (sortCol === "due_date") {
        const ea = !a.due_date, eb = !b.due_date;
        if (ea !== eb) return ea ? 1 : -1;
      }
      const va = fn(a), vb = fn(b);
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  })();

  // CR-9: Unique sales rep names from current orders
  // FOLLOW-UP 3 (8e): every rep of every order this user may see, not only the loaded page
  const [salesReps, setSalesReps] = useState([]);
  useEffect(() => {
    api.get("/orders/sales-reps").then(r => setSalesReps(r.data?.data || [])).catch(() => setSalesReps([]));
  }, []);

  // CR-10: Quick date presets
  const applyDatePreset = (key) => {
    setDatePreset(key);
    const now = new Date(); const today = istToday();
    if (key === "today") { setDateFrom(today); setDateTo(today); }
    else if (key === "this_week") { const d = new Date(now); d.setDate(d.getDate() - d.getDay()); setDateFrom(istDaysAgo(now.getDay())); setDateTo(today); }
    else if (key === "this_month") { setDateFrom(today.slice(0, 8) + "01"); setDateTo(today); }
    else if (key === "last_month") { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); setDateFrom(d.toISOString().slice(0, 10)); setDateTo(e.toISOString().slice(0, 10)); }
    else if (key === "this_quarter") { const q = Math.floor(now.getMonth() / 3) * 3; setDateFrom(`${now.getFullYear()}-${String(q + 1).padStart(2, '0')}-01`); setDateTo(today); }
    else if (key === "this_year") { setDateFrom(`${now.getFullYear()}-01-01`); setDateTo(today); }
  };

  const doAction = async (newStatus, extraFields = {}) => {
    if (newStatus === "Rejected" && !actionNote) { toast.error("Rejection reason is required"); return; }
    try {
      await api.put(`/orders/${selectedOrder._id}/status`, { status: newStatus, note: actionNote, awb_number: actionAwb || "", courier_name: actionCourier || "", ...extraFields });
      toast.success(`Order updated to ${newStatus}`);
      const { data } = await api.get(`/orders/${selectedOrder._id}`);
      setSelectedOrder(data); loadOrders();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // ─── DETERMINE AVAILABLE ACTIONS FOR CURRENT USER ─────────
  const getActions = (o) => {
    if (!o) return [];
    const s = o.status;
    const isAdmin = roles.includes("Admin") || roles.includes("admin");
    const isManager = roles.includes("Sales Manager");
    const isFinanceExec = roles.includes("Finance Executive");
    const isFinanceMgr = roles.includes("Finance Manager");
    const isFinance = isFinanceExec || isFinanceMgr;
    const isWhExec = roles.includes("Warehouse Executive");
    const isWhMgr = roles.includes("Warehouse Manager");
    const isWh = isWhExec || isWhMgr;
    const isLogistics = roles.includes("Logistics Manager") || roles.includes("Logistics Coordinator") || roles.includes("Logistics Executive");
    const isInvoicing = roles.includes("Finance — Invoicing Executive");
    const actions = [];

    // Pre-finance stages
    if (s === "Pending with Manager" && (isManager || isAdmin)) {
      actions.push({ label: "Approve — Send to Finance", status: "Pending with Finance", variant: "default", icon: Check });
      actions.push({ label: "Forward for Admin Discount Approval", status: "Pending with Admin", variant: "outline" });
      actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X, needsNote: true });
    }
    if (s === "Pending with Admin" && isAdmin) {
      actions.push({ label: "Approve Discount — Send to Finance", status: "Pending with Finance", variant: "default", icon: Check });
      actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X, needsNote: true });
    }
    if (s === "Pending with Finance" && (isFinance || isAdmin)) {
      actions.push({ label: "Approve — Send to Warehouse", status: "At Warehouse", variant: "default", icon: Package });
      actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X, needsNote: true });
    }

    // Post-finance warehouse pipeline
    if (s === "At Warehouse" && (isWh || isAdmin)) {
      actions.push({ label: "Start Processing", status: "Under Processing", variant: "default", icon: Package, stageForm: "warehouse_processing" });
    }
    if (s === "Under Processing" && (isWh || isAdmin)) {
      actions.push({ label: "Mark as Ready for Dispatch", status: "Ready for Dispatch", variant: "default", icon: ClipboardCheck, needsBoxes: true, stageForm: "ready_for_dispatch" });
    }
    if (s === "Ready for Dispatch" && (isLogistics || isAdmin)) {
      actions.push({ label: "Assign Vehicle", status: "Vehicle Assigned", variant: "default", icon: Truck, stageForm: "vehicle_assigned" });
    }
    if (s === "Vehicle Assigned" && (isInvoicing || isFinanceMgr || isAdmin)) {
      actions.push({ label: "Upload Invoice — Ready to Dispatch", status: "Invoice Uploaded", variant: "default", stageForm: "invoice_uploaded" });
    }
    if (s === "Invoice Uploaded" && (isWh || isAdmin)) {
      actions.push({ label: "Confirm Dispatched", status: "Dispatched", variant: "default", icon: Truck, needsAwb: true, stageForm: "dispatched" });
    }
    if (s === "Dispatched" && (isLogistics || isWhMgr || isAdmin)) {
      actions.push({ label: "Confirm Delivered (GRN Received)", status: "Delivered", variant: "default", icon: Check, stageForm: "delivered" });
    }
    if (s === "Delivered" && (isFinance || isAdmin)) {
      const canClose = (o.outstanding_balance || 0) <= 0;
      if (canClose) actions.push({ label: "Request Order Closure", status: "Closure Pending", variant: "outline" });
    }
    if ((s === "Closure Pending" || s === "Closure Requested") && (isFinanceMgr || isAdmin)) {
      actions.push({ label: "Approve & Close Order", status: "Order Closed", variant: "default", icon: Check });
    }

    // Legacy stage support
    if (s === "Ready for Pickup" && (isWh || isAdmin)) {
      actions.push({ label: "Mark as Shipped", status: "Shipped", variant: "default", icon: Truck, needsAwb: true });
    }
    if (s === "Shipped" && (isWhMgr || isAdmin)) {
      actions.push({ label: "Confirm Delivered", status: "Delivered", variant: "default", icon: Check });
    }

    // Rejection resubmit
    if (s === "Rejected") {
      const isPuncher = o.created_by === user?._id || o.created_by === user?.id;
      if (isPuncher || isAdmin) actions.push({ label: "Edit & Resubmit", status: "Pending with Manager", variant: "outline", isResubmit: true });
    }

    // Admin cancel (before warehouse processing starts)
    if (isAdmin && !["Under Processing", "Ready for Dispatch", "Vehicle Assigned", "Invoice Uploaded", "Dispatched", "Delivered", "Closure Pending", "Order Closed", "Cancelled"].includes(s)) {
      actions.push({ label: "Cancel Order", status: "Cancelled", variant: "destructive", needsNote: true });
    }
    return actions;
  };

  return (
    <div data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Orders</h1><p className="text-sm text-zinc-500 mt-1">{total} orders</p></div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-orders-btn" onClick={() => {
            const params = new URLSearchParams();
            // the download must match what is on screen, so every live filter goes with it
            if (search) params.set("search", search);
            if (statusFilter) params.set("status", statusFilter);
            if (specialFilter) params.set("filter", specialFilter);
            if (effectiveChannelId) params.set("channel_id", effectiveChannelId);
            if (customerTypeFilter) params.set("customer_type", customerTypeFilter);
            if (paymentFilter) params.set("payment_status", paymentFilter);
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            if (salesRepFilter) params.set("sales_rep_id", salesRepFilter);   // FOLLOW-UP 3 (8e)
            params.set("export", "excel");
            api.get(`/orders?${params.toString()}`, { responseType: "blob" }).then(r => {
              const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
              const a = document.createElement("a"); a.href = url; a.download = `Orders_Export_${istToday()}.xlsx`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success("Orders exported");
            }).catch(() => toast.error("Export failed"));
          }}><Download size={14} /> Export</Button>
          {(roles.includes("Sales Executive") || roles.includes("Sales Manager") || roles.includes("Admin") || roles.includes("admin")) && (
            <Button onClick={() => navigate("/orders/new")} className="rounded-sm h-8 text-xs" data-testid="create-order-button"><Plus size={14} /> New Order</Button>
          )}
        </div>
      </div>

      {/* Active filter banner from dashboard drill-down */}
      {(specialFilter || statusFilter) && (
        <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between" data-testid="filter-banner">
          <span className="text-xs text-blue-800 font-medium">
            Filtered: {specialFilter === "overdue" ? "Overdue Payments (past due date, balance unpaid)" : specialFilter === "outstanding" ? "Outstanding Balance > 0" : specialFilter === "pending" ? "Pending Approvals" : specialFilter === "ready_to_close" ? "Ready to Close (Delivered + Fully Paid)" : specialFilter === "dispatched_today" ? "Dispatched Today" : specialFilter === "inactive_customers" ? "Inactive Customers (30+ days, no orders)" : specialFilter === "grn_not_delivered" ? "GRN or delivery date recorded, not yet marked Delivered" : specialFilter === "delivered_before_dispatch" ? "Delivery date earlier than the dispatch date recorded in ORBIT" : statusFilter ? `Status: ${statusFilter}` : ""}
          </span>
          <Button variant="ghost" className="h-6 text-[10px] text-blue-700 hover:text-blue-900" onClick={() => navigate("/orders")} data-testid="clear-filter-btn">Clear Filter</Button>
        </div>
      )}

      {/* Comprehensive Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-end" data-testid="order-filters">
        <div className="relative w-52">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search order # or customer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="orders-search-input" />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Status</span>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8 text-xs rounded-sm" data-testid="orders-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Trade Format</span>
          <MultiSelectDropdown label="Trade Format" options={TRADE_FORMATS} selected={tradeFormatFilter}
            onChange={v => { setTradeFormatFilter(v); setChannelMultiFilter(prev => prev.filter(id => filteredChannels.some(c => c._id === id))); }} />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Sales Channel</span>
          <MultiSelectDropdown label="Sales Channel" options={channelOptions} selected={channelMultiFilter}
            onChange={setChannelMultiFilter} groupBy="group" />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Payment</span>
          <Select value={paymentFilter || "all"} onValueChange={(v) => setPaymentFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 h-8 text-xs rounded-sm" data-testid="orders-payment-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{PAYMENT_STATUSES.map(ps => <SelectItem key={ps} value={ps}>{ps}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Customer Type</span>
          <Select value={customerTypeFilter || "all"} onValueChange={(v) => setCustomerTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 h-8 text-xs rounded-sm" data-testid="orders-ctype-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{CUSTOMER_TYPES.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Sales Rep</span>
          <Select value={salesRepFilter || "all"} onValueChange={(v) => { setPage(1); setSalesRepFilter(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-36 h-8 text-xs rounded-sm" data-testid="orders-rep-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{salesReps.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Date From</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs rounded-sm w-32" data-testid="orders-date-from" />
        </div>
        <div>
          <span className="text-[9px] font-semibold uppercase text-zinc-400 block mb-0.5">Date To</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs rounded-sm w-32" data-testid="orders-date-to" />
        </div>
        <Button variant={specialFilter === "grn_not_delivered" ? "default" : "outline"}
                className="rounded-sm h-8 text-xs" data-testid="filter-grn-not-delivered"
                onClick={() => { setPage(1); setSpecialFilter(specialFilter === "grn_not_delivered" ? "" : "grn_not_delivered"); }}>
          GRN in, not Delivered
        </Button>
        <Button variant={specialFilter === "delivered_before_dispatch" ? "default" : "outline"}
                className="rounded-sm h-8 text-xs" data-testid="filter-delivered-before-dispatch"
                onClick={() => setSpecialFilter(specialFilter === "delivered_before_dispatch" ? "" : "delivered_before_dispatch")}>
          Delivered before dispatch date
        </Button>
        {(statusFilter || channelFilter || paymentFilter || dateFrom || dateTo || salesRepFilter || tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && (
          <Button variant="ghost" className="h-8 text-[10px] text-zinc-500" onClick={() => { setStatusFilter(""); setChannelFilter(""); setPaymentFilter(""); setDateFrom(""); setDateTo(""); setSalesRepFilter(""); setDatePreset(""); setTradeFormatFilter([]); setChannelMultiFilter([]); }} data-testid="clear-all-filters">Clear All</Button>
        )}
      </div>
      {(tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && (
        <div className="flex flex-wrap gap-1 mb-2">
          <SelectedTags selected={tradeFormatFilter} options={TRADE_FORMATS} onRemove={v => setTradeFormatFilter(tradeFormatFilter.filter(x => x !== v))} />
          <SelectedTags selected={channelMultiFilter} options={channelOptions} onRemove={v => setChannelMultiFilter(channelMultiFilter.filter(x => x !== v))} />
        </div>
      )}

      {/* CR-10: Quick date presets */}
      <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="date-presets">
        {[{k:"today",l:"Today"},{k:"this_week",l:"This Week"},{k:"this_month",l:"This Month"},{k:"last_month",l:"Last Month"},{k:"this_quarter",l:"This Quarter"},{k:"this_year",l:"This Year"}].map(p => (
          <Button key={p.k} variant={datePreset === p.k ? "default" : "outline"} onClick={() => applyDatePreset(p.k)} className={`h-6 text-[10px] px-2 rounded-sm ${datePreset === p.k ? 'bg-zinc-900 text-white' : ''}`}>{p.l}</Button>
        ))}
      </div>

      {/* Inactive Customers View (special filter) */}
      {specialFilter === "inactive_customers" ? (
        <div className="border border-zinc-200 overflow-x-auto" data-testid="inactive-customers-table">
          <table className="data-table w-full">
            <thead><tr><th>Customer</th><th>Company</th><th>Last Order Date</th><th className="num">Last Order Value</th><th className="num">Days Inactive</th><th>Phone</th></tr></thead>
            <tbody>
              {inactiveLoading ? <tr><td colSpan={6} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
              inactiveCustomers.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-xs">
                <p className="text-sm font-medium text-green-600 mb-1">All customers are active!</p>
                <p className="text-zinc-400">No customers beyond 30+ days without placing an order.</p>
              </td></tr> :
              inactiveCustomers.map((c, i) => (
                <tr key={i} data-testid={`inactive-row-${i}`}>
                  <td className="font-medium text-zinc-900 text-xs">{c.name}</td>
                  <td className="text-zinc-600 text-xs">{c.company || '-'}</td>
                  <td className="font-mono text-xs text-zinc-500">{c.last_order_date ? fmtDate(c.last_order_date) : 'Never ordered'}</td>
                  <td className="num font-mono text-xs">{c.last_order_value ? fmt(c.last_order_value) : '-'}</td>
                  <td className="num font-mono text-xs font-semibold text-amber-600">{c.days_inactive === 999 ? 'Never' : `${c.days_inactive}d`}</td>
                  <td className="font-mono text-xs text-zinc-500">{c.phone || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <>
      {/* Advanced Search Panel */}
      <div className="mb-3 flex items-center gap-2">
        <Button variant={showAdvancedSearch ? "default" : "outline"} className="rounded-sm h-7 text-[10px]" onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}>Advanced Search</Button>
        {bulkSearchActive && <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-sm">Showing {total} orders from bulk search</span>}
        {bulkSearchActive && <Button variant="ghost" className="rounded-sm h-6 text-[10px] text-blue-600" onClick={() => { setBulkSearchActive(false); setBulkSearchText(""); setShowAdvancedSearch(false); }}>Clear Advanced Search</Button>}
      </div>
      {showAdvancedSearch && !bulkSearchActive && (
        <div className="mb-4 p-3 border border-zinc-200 bg-zinc-50 rounded-sm" data-testid="advanced-search-panel">
          <Label className="text-xs font-semibold">Search multiple orders</Label>
          <textarea value={bulkSearchText} onChange={e => setBulkSearchText(e.target.value)} className="mt-1 w-full rounded-sm border border-zinc-200 p-2 text-xs font-mono" rows={3} placeholder="Enter order numbers separated by commas or spaces&#10;e.g. ORD-00001, ORD-00042 ORD-00078" />
          <Button className="rounded-sm h-7 text-xs mt-2" onClick={() => { if (bulkSearchText.trim()) setBulkSearchActive(true); }} disabled={!bulkSearchText.trim()}>Search Orders</Button>
        </div>
      )}

      {/* Orders — Mobile Card Layout + Desktop Table */}
      <div>
        {/* Mobile cards — visible below md */}
        <div className="md:hidden space-y-2" data-testid="orders-mobile-cards">
          {loading ? <p className="text-center py-8 text-zinc-400 text-sm">Loading...</p> :
          orders.length === 0 ? <p className="text-center py-12 text-zinc-400 text-sm">{specialFilter ? "No orders match this filter" : "No orders found"}</p> :
          orders.map(o => (
            <div key={o._id} onClick={() => openDetail(o)} className="mobile-order-card bg-white border border-zinc-200 rounded-sm" style={{borderLeftColor: SC[o.status]?.includes('green') ? '#16a34a' : SC[o.status]?.includes('amber') ? '#d97706' : SC[o.status]?.includes('red') ? '#dc2626' : SC[o.status]?.includes('blue') ? '#2563eb' : '#a1a1aa'}} data-testid={`order-card-${o._id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold text-zinc-900">{o.order_number}</span>
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${SC[o.status] || ''}`}>{o.status}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-zinc-700">{o.customer_name}</span>
                <span className="text-xs text-zinc-500">{o.channel_name || ''}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-zinc-500">{fmtDate(o.order_date)}</span>
                <span className="font-mono text-sm font-bold">{fmt(o.invoice_value)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {o.payment_status && <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${PC[o.payment_status] || ''}`}>{o.payment_status}</span>}
                {o.customer_type && <span className="text-[10px] text-zinc-400">{o.customer_type}</span>}
                {o.is_private && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-purple-100 text-purple-700 rounded-sm">Private</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table — hidden below md */}
        <div className="hidden md:block border border-zinc-200 overflow-x-auto">
        <table className="data-table w-full" data-testid="orders-table">
          <thead><tr><SH col="order">Order #</SH><SH col="customer">Customer</SH><SH col="customer_type">Customer Type</SH><th>Sales Rep</th><th>Channel</th><th>Warehouse</th><th>Date</th><SH col="dispatch_by">Dispatch By</SH><SH col="po_expiry">PO Expiry</SH><th>PO #</th><SH col="priority">Priority</SH><th className="num">Invoice</th><th>Status</th><th>Payment</th><SH col="due_date">Due Date</SH><th className="w-20"></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={14} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            displayOrders.length === 0 ? <tr><td colSpan={14} className="text-center py-12 text-zinc-400 text-xs">
              {specialFilter ? "No orders match this filter" : "No orders found"}
            </td></tr> :
            displayOrders.map(o => (
              <tr key={o._id} className="cursor-pointer" onClick={() => openDetail(o)} data-testid={`order-row-${o._id}`}>
                <td className="font-mono text-xs font-semibold text-zinc-900">{o.order_number}{(o.priority === "urgent") && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-800 rounded-sm">Urgent</span>}{(o.priority === "critical") && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-red-100 text-red-800 rounded-sm">Critical</span>}</td>
                <td className="text-zinc-700 text-xs">{o.customer_name}</td>
                <td className="text-[10px] text-zinc-500">{o.customer_type || '\u2014'}</td>
                <td className="text-[10px] text-zinc-500" data-testid={`order-sales-rep-${o.order_number}`}>{o.sales_rep_name || o.created_by_name || '\u2014'}</td>
                <td className="text-xs">{o.channel_name || '-'}</td>
                <td className="text-[10px] text-zinc-500">{o.warehouse_name || '\u2014'}</td>
                <td className="font-mono text-xs text-zinc-500">{fmtDate(o.order_date)}</td>
                <td className="font-mono text-xs text-zinc-500">{o.dispatch_date ? fmtDate(o.dispatch_date) : '\u2014'}</td>
                <td className="text-xs">{(() => {
                  if (o.po_expiry_not_available) return <span className="text-zinc-400">N/A</span>;
                  if (!o.po_expiry_date) return '\u2014';
                  const past = ["Dispatched","Delivered","Closure Pending","Order Closed"].includes(o.status);
                  if (past) return <span className="text-zinc-400 font-mono">{fmtDate(o.po_expiry_date)}</span>;
                  const days = Math.ceil((new Date(o.po_expiry_date) - new Date()) / 86400000);
                  const c = days <= 3 ? "text-red-600 font-semibold" : days <= 7 ? "text-amber-600" : "text-green-600";
                  return <span className={`font-mono ${c}`}>{fmtDate(o.po_expiry_date)}</span>;
                })()}</td>
                <td className="font-mono text-[10px] text-zinc-500">{o.po_number || '\u2014'}</td>
                <td>{o.priority && o.priority !== "standard" && <span className={`px-1.5 py-0.5 text-[8px] font-semibold rounded-sm ${PRIO_BADGE[o.priority] || ''}`}>{o.priority}</span>}</td>
                <td className="num font-mono text-xs font-semibold">{fmt(o.invoice_value)}</td>
                <td><span className={`inline-block px-2 py-0.5 text-[9px] font-semibold rounded-sm ${SC[o.status] || ''}`}>{o.status}</span>{o.is_private && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-bold bg-purple-100 text-purple-700 rounded-sm">Private</span>}{(o.intended_cn_applications || []).length > 0 && (roles.includes("Finance Manager") || roles.includes("Finance Executive") || roles.includes("Finance — Invoicing Executive") || roles.includes("Admin") || roles.includes("admin")) && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded-sm">CN Applied</span>}</td>
                <td>{o.payment_status && <span className={`inline-block px-2 py-0.5 text-[9px] font-semibold rounded-sm ${PC[o.payment_status] || ''}`}>{o.payment_status}</span>}{(o.adjusted_amount || 0) > 0 && <span className="ml-1 font-mono text-[9px] text-amber-700 whitespace-nowrap" data-testid={`adjusted-${o._id}`} title={o.adjusted_reason || "Adjusted on short-payment closure"}>adj {fmt(o.adjusted_amount)}</span>}</td>
                <td className="text-xs">{(() => {
                  if (!o.due_date) return '\u2014';
                  if (o.payment_status === 'Fully Paid') return <span className="text-zinc-400 line-through font-mono">{fmtDate(o.due_date)}</span>;
                  const d = Math.ceil((new Date(o.due_date) - new Date()) / 86400000);
                  const c = d < 0 ? "text-red-600 font-semibold" : d <= 7 ? "text-amber-600" : "text-green-600";
                  return <span className={`font-mono ${c}`}>{fmtDate(o.due_date)}</span>;
                })()}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); openDetail(o); }} className="w-8 h-8 flex items-center justify-center hover:bg-zinc-100 rounded-sm transition-colors" title="View order details"><Eye size={15} className="text-zinc-500" /></button>
                    {["Draft", "Pending with Manager", "Pending with Finance", "Pending with Admin"].includes(o.status) && (roles.includes("Sales Executive") || roles.includes("Sales Manager") || roles.includes("Admin") || roles.includes("admin")) && (
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await api.get(`/orders/${o._id}/proforma-invoice`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                          const a = document.createElement('a'); a.href = url; a.download = `PI-${o.order_number}.pdf`; a.click();
                          window.URL.revokeObjectURL(url);
                          toast.success("PI downloaded");
                        } catch { toast.error("Failed to generate PI"); }
                      }} className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 rounded-sm transition-colors" title="Generate Proforma Invoice" data-testid={`pi-btn-${o._id}`}><FileText size={15} className="text-blue-500" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
      </>
      )}


      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4" data-testid="orders-pagination">
          <p className="text-xs text-zinc-500">Showing {Math.min((page - 1) * 50 + 1, total)}–{Math.min(page * 50, total)} of {total} orders</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm h-7 text-xs" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            {Array.from({ length: Math.min(Math.ceil(total / 50), 7) }, (_, i) => {
              const maxPages = Math.ceil(total / 50);
              let pageNum;
              if (maxPages <= 7) { pageNum = i + 1; }
              else if (page <= 4) { pageNum = i + 1; }
              else if (page >= maxPages - 3) { pageNum = maxPages - 6 + i; }
              else { pageNum = page - 3 + i; }
              return (
                <Button key={pageNum} variant={pageNum === page ? "default" : "outline"} className="rounded-sm h-7 w-7 text-xs p-0" onClick={() => setPage(pageNum)}>{pageNum}</Button>
              );
            })}
            <Button variant="outline" className="rounded-sm h-7 text-xs" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}


      {/* ═══ ORDER DETAIL DIALOG WITH PIPELINE ═══ */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="order-detail-dialog">
          {selectedOrder && (() => {
            const o = selectedOrder;
            const actions = getActions(o);
            const r2 = (n) => Math.round((n || 0) * 100) / 100;
            return (
            <div>
              {/* Sticky Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-heading font-mono text-xl font-bold">{o.order_number}</span>
                    <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-sm ${SC[o.status] || ''}`}>{o.status}</span>
                    {o.payment_status && <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-sm ${PC[o.payment_status] || ''}`}>{o.payment_status}</span>}
                  </div>
                  <button onClick={() => setShowDetail(false)} className="p-1 hover:bg-zinc-100 rounded-sm"><X size={16} /></button>
                </div>
                {/* Status Timeline */}
                <div className="mt-3">
                  <StatusTimeline currentStatus={o.status} statusHistory={o.status_history} pipelineStages={PIPELINE_STAGES} />
                </div>
              </div>

              <div className="px-6 py-4 space-y-5">
                {/* Order Info Grid */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Customer</p><p className="text-xs font-medium">{o.customer_name}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Channel</p><p className="text-xs">{o.channel_name || '-'}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Date</p><p className="text-xs font-mono">{fmtDate(o.order_date)}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Type</p><p className="text-xs">{o.order_type}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Created By</p><p className="text-xs">{o.created_by_name || '-'}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Invoice</p><p className="text-sm font-mono font-bold">{fmt(o.invoice_value)}</p></div>
                </div>

                <Tabs defaultValue="items" className="space-y-3">
                  <TabsList className="bg-zinc-100 rounded-sm h-8">
                    <TabsTrigger value="items" className="text-xs rounded-sm h-6">Items</TabsTrigger>
                    <TabsTrigger value="payment" className="text-xs rounded-sm h-6">Payment</TabsTrigger>
                    <TabsTrigger value="logistics" className="text-xs rounded-sm h-6">Logistics</TabsTrigger>
                    <TabsTrigger value="audit" className="text-xs rounded-sm h-6">Audit Trail</TabsTrigger>
                  </TabsList>

                  {/* ITEMS TAB */}
                  <TabsContent value="items">
                    <div className="border border-zinc-200 overflow-x-auto">
                      <table className="data-table w-full">
                        <thead><tr><th>SKU</th><th>Name</th><th className="num">Qty</th><th className="num">MRP</th><th className="num">Disc%</th><th className="num">Disc Amt</th><th className="num">GST%</th><th className="num">GST Amt</th><th className="num">Net Amt</th></tr></thead>
                        <tbody>
                          {(o.line_items || []).map((li, i) => {
                            const dAmt = r2((li.mrp || li.selling_price || 0) * (li.discount_percent || 0) / 100 * (li.quantity || 1));
                            return (
                            <tr key={i}>
                              <td className="font-mono text-xs">{li.sku_id}</td>
                              <td className="text-xs">{li.sku_name}</td>
                              <td className="num font-mono">{li.quantity}</td>
                              <td className="num font-mono">{fmt(li.mrp || li.selling_price)}</td>
                              <td className="num font-mono">{li.discount_percent || 0}%</td>
                              <td className="num font-mono text-red-600">{dAmt > 0 ? `-${fmt(dAmt)}` : '—'}</td>
                              <td className="num font-mono">{li.gst_percent}%</td>
                              <td className="num font-mono">{fmt(li.gst_amount)}</td>
                              <td className="num font-mono font-semibold">{fmt(li.net_amount)}</td>
                            </tr>);
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(o.foc_items || []).length > 0 && (
                      <div className="mt-3 border border-dashed border-green-300 bg-green-50 rounded-sm divide-y divide-green-200">
                        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase text-green-700">FOC Stock</div>
                        {o.foc_items.map((f, i) => (
                          <div key={i} className="px-3 py-1.5 flex justify-between text-xs">
                            <span className="text-green-800">{f.sku_name} x{f.quantity}</span>
                            <span className="text-green-700 font-semibold">FREE {f.mrp ? `(MRP: ${fmt(f.mrp * f.quantity)})` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Totals */}
                    <div className="mt-3 border border-zinc-200 p-3 max-w-sm ml-auto">
                      {o.total_discount > 0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">Total Discount</span><span className="font-mono text-xs text-red-600">-{fmt(o.total_discount)}</span></div>}
                      <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">Taxable</span><span className="font-mono text-xs">{fmt(o.taxable_amount)}</span></div>
                      {(o.cgst || 0) > 0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">CGST</span><span className="font-mono text-xs">{fmt(o.cgst)}</span></div>}
                      {(o.sgst || 0) > 0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">SGST</span><span className="font-mono text-xs">{fmt(o.sgst)}</span></div>}
                      {(o.igst || 0) > 0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">IGST</span><span className="font-mono text-xs">{fmt(o.igst)}</span></div>}
                      <div className="flex justify-between pt-2 border-t border-zinc-200"><span className="text-sm font-bold">Invoice Total</span><span className="font-mono text-sm font-bold">{fmt(o.invoice_value)}</span></div>
                    </div>
                  </TabsContent>

                  {/* PAYMENT TAB */}
                  <TabsContent value="payment">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="border border-zinc-200 p-3 space-y-2 text-xs">
                        <p className="text-[10px] font-semibold uppercase text-zinc-400">Payment Info</p>
                        <div className="flex justify-between"><span className="text-zinc-500">Type</span><span className="font-medium">{o.payment_type === 'full_payment' ? 'Full Payment' : o.payment_type === 'part_advance' ? 'Part Advance' : o.payment_type === 'credit_sale' ? 'Credit Sale' : o.payment_type || '—'}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Amount Paid</span><span className="font-mono font-semibold text-green-700">{fmt(o.amount_paid || 0)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Outstanding</span><span className={`font-mono font-semibold ${(o.outstanding_balance || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>{fmt(o.outstanding_balance || 0)}</span></div>
                        {o.expected_payment_date && <div className="flex justify-between"><span className="text-zinc-500">Due Date</span><span className="font-mono font-medium">{fmtDate(o.expected_payment_date)}</span></div>}
                        {o.payment_mode && <div className="flex justify-between"><span className="text-zinc-500">Mode</span><span>{o.payment_mode}</span></div>}
                        {o.payment_reference && <div className="flex justify-between"><span className="text-zinc-500">Reference</span><span className="font-mono">{o.payment_reference}</span></div>}
                        {o.payment_date && <div className="flex justify-between"><span className="text-zinc-500">Receipt Date</span><span className="font-mono">{fmtDate(o.payment_date)}</span></div>}
                      </div>
                      <div className="border border-zinc-200 p-3 text-xs">
                        <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Payment Receipts</p>
                        {(o.payments || []).length === 0 ? <p className="text-zinc-400">No receipts recorded</p> :
                        (o.payments || []).map((p, i) => (
                          <div key={i} className="flex justify-between py-1 border-b border-zinc-50 last:border-0">
                            <div><span className="font-mono font-semibold">{fmt(p.amount)}</span> <span className="text-zinc-400">via {p.mode}</span></div>
                            <span className="text-zinc-500 font-mono">{fmtDate(p.date)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  {/* LOGISTICS & STAGE DATA TAB */}
                  <TabsContent value="logistics">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div className="border border-zinc-200 p-3"><span className="text-zinc-400 block text-[10px] mb-1">Warehouse</span>{o.warehouse_name || '-'}</div>
                      <div className="border border-zinc-200 p-3"><span className="text-zinc-400 block text-[10px] mb-1">Dispatch Date</span>{fmtDate(o.dispatch_date || o.dispatch_data?.actual_dispatch_date) || '-'}</div>
                      <div className="border border-zinc-200 p-3"><span className="text-zinc-400 block text-[10px] mb-1">Courier</span>{o.courier_partner || o.logistics?.transporter_name || '-'}</div>
                      <div className="border border-zinc-200 p-3"><span className="text-zinc-400 block text-[10px] mb-1">AWB / LR</span><span className="font-mono">{o.awb_number || o.logistics?.lr_number || '-'}</span></div>
                    </div>

                    {/* Vehicle & Logistics details from stage data */}
                    {o.logistics && (
                      <div className="mt-3 border border-zinc-200 p-3">
                        <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Vehicle & Transport</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-zinc-700">
                          {o.logistics.transport_type && <div><span className="text-zinc-400">Type:</span> {o.logistics.transport_type}</div>}
                          {o.logistics.vehicle_number && <div><span className="text-zinc-400">Vehicle:</span> {o.logistics.vehicle_number}</div>}
                          {o.logistics.driver_name && <div><span className="text-zinc-400">Driver:</span> {o.logistics.driver_name}</div>}
                          {o.logistics.freight_cost && <div><span className="text-zinc-400">Freight:</span> <span className="font-mono">{fmt(o.logistics.freight_cost)}</span></div>}
                          {o.logistics.freight_terms && <div><span className="text-zinc-400">Freight Terms:</span> {o.logistics.freight_terms}</div>}
                        </div>
                      </div>
                    )}

                    {/* Invoice data */}
                    {o.invoice_data && (
                      <div className="mt-3 border border-zinc-200 p-3">
                        <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Invoice & E-Way Bill</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-700">
                          {o.invoice_data.invoice_number && <div><span className="text-zinc-400">Invoice #:</span> <span className="font-mono font-semibold">{o.invoice_data.invoice_number}</span></div>}
                          {o.invoice_data.invoice_date && <div><span className="text-zinc-400">Invoice Date:</span> {fmtDate(o.invoice_data.invoice_date)}</div>}
                          {o.invoice_data.eway_bill_number && <div><span className="text-zinc-400">E-Way Bill:</span> <span className="font-mono">{o.invoice_data.eway_bill_number}</span></div>}
                          {o.invoice_data.eway_validity && <div><span className="text-zinc-400">E-Way Validity:</span> {o.invoice_data.eway_validity}</div>}
                        </div>
                        {!o.invoice_data.invoice_number && <p className="text-[10px] text-amber-600 mt-1">Invoice pending — Generate from Tally (Coming Soon)</p>}
                      </div>
                    )}

                    {/* Delivery data */}
                    {o.delivery_data && (
                      <div className="mt-3 border border-zinc-200 p-3">
                        <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Delivery Confirmation</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-700">
                          {o.delivery_data.delivery_date && <div><span className="text-zinc-400">Delivered:</span> {fmtDate(o.delivery_data.delivery_date)}</div>}
                          {o.delivery_data.grn_number && <div><span className="text-zinc-400">GRN #:</span> <span className="font-mono">{o.delivery_data.grn_number}</span></div>}
                          {o.delivery_data.delivered_to && <div><span className="text-zinc-400">Received by:</span> {o.delivery_data.delivered_to}</div>}
                          {o.delivery_data.partial_delivery && <div className="text-red-600 font-semibold">Partial Delivery Reported</div>}
                        </div>
                        {o.delivery_data.remarks && <p className="text-xs text-zinc-600 mt-1 italic">"{o.delivery_data.remarks}"</p>}
                      </div>
                    )}

                    {/* Instructions thread */}
                    <div className="mt-3 border border-zinc-200 rounded-sm">
                      <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-400">Instructions & Notes Thread</div>
                      {(o.instructions_thread || o.order_notes || []).length === 0 && (!o.special_instructions && !o.payment_notes) ? (
                        <p className="px-3 py-3 text-xs text-zinc-400">No notes yet</p>
                      ) : (
                        <div className="divide-y divide-zinc-50 max-h-48 overflow-y-auto">
                          {o.special_instructions && <div className="px-3 py-2"><span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-700">Logistics</span><p className="text-xs text-zinc-700 mt-0.5">{o.special_instructions}</p></div>}
                          {(o.instructions_thread || []).map((n, i) => (
                            <div key={`t-${i}`} className="px-3 py-2">
                              <div className="flex items-center gap-2"><span className="text-[10px] font-semibold text-zinc-700">{n.author}</span><span className="text-[9px] text-zinc-400">{n.role}</span><span className="text-[9px] text-zinc-400">{fmtDateTime(n.timestamp)}</span></div>
                              <p className="text-xs text-zinc-700 mt-0.5">{n.note}</p>
                            </div>
                          ))}
                          {(o.order_notes || []).map((n, i) => (
                            <div key={`n-${i}`} className="px-3 py-2">
                              <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-sm ${n.section === 'FOC Stock' ? 'bg-green-100 text-green-700' : n.section === 'Payment Terms' ? 'bg-blue-100 text-blue-700' : 'bg-zinc-100 text-zinc-600'}`}>{n.section}</span>
                              {n.timestamp && <span className="text-[10px] text-zinc-400 ml-2">{fmtDateTime(n.timestamp)}</span>}
                              <p className="text-xs text-zinc-700 mt-0.5">{n.note}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* AUDIT TRAIL TAB */}
                  <TabsContent value="audit">
                    <div className="space-y-2">
                      {(o.status_history || []).map((sh, i) => (
                        <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-50">
                          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${SC[sh.status]?.split(' ')[0] || 'bg-zinc-400'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${SC[sh.status] || 'bg-zinc-100 text-zinc-600'}`}>{sh.status}</span>
                              {sh.previous_status && <span className="text-[10px] text-zinc-400">from {sh.previous_status}</span>}
                            </div>
                            <p className="text-[10px] text-zinc-500 mt-0.5">{sh.user_name} ({sh.role || '-'}) &middot; {fmtDateTime(sh.timestamp)}</p>
                            {sh.note && <p className="text-xs text-zinc-700 mt-0.5 italic">"{sh.note}"</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>

                {/* ═══ ACTION BUTTONS — ROLE-BASED ═══ */}
                {actions.length > 0 && (
                  <div className="border-t border-zinc-200 pt-4 space-y-3" data-testid="order-actions">
                    <p className="text-[10px] font-semibold uppercase text-zinc-400">Actions Available to You ({role})</p>

                    {/* Note/Reason field — shown if any action needs it */}
                    <div>
                      <Label className="text-xs">Note / Reason</Label>
                      <Textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Add a note (required for rejections)" className="mt-1 rounded-sm text-xs" rows={2} data-testid="action-note" />
                    </div>

                    {/* AWB fields for shipping */}
                    {actions.some(a => a.needsAwb) && (
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Courier / Logistics Partner</Label><Input value={actionCourier} onChange={(e) => setActionCourier(e.target.value)} className="mt-1 rounded-sm" data-testid="action-courier" /></div>
                        <div><Label className="text-xs">AWB / Tracking Number</Label><Input value={actionAwb} onChange={(e) => setActionAwb(e.target.value)} className="mt-1 rounded-sm font-mono" data-testid="action-awb" /></div>
                      </div>
                    )}

                    {/* Boxes for packing */}
                    {actions.some(a => a.needsBoxes) && (
                      <div><Label className="text-xs">Number of Boxes / Cartons Packed</Label><Input value={actionBoxes} onChange={(e) => setActionBoxes(e.target.value)} className="mt-1 rounded-sm w-48" data-testid="action-boxes" /></div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      {actions.map((a, i) => (
                        <Button key={i} variant={a.variant || "default"}
                          onClick={() => {
                            if (a.needsAwb && !actionAwb) { toast.error("AWB/Tracking number is required"); return; }
                            if (a.needsBoxes && !actionBoxes) { toast.error("Number of boxes is required"); return; }
                            doAction(a.status);
                          }}
                          className="rounded-sm text-xs" data-testid={`action-${a.status.toLowerCase().replace(/\s+/g, '-')}`}>
                          {a.icon && <a.icon size={12} />} {a.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
